// Address parsing utility for US addresses
import { GoogleAddressService } from './googleAddressService';
import { ZipCodeLookup } from './zipCodeLookup';

export interface ParsedAddress {
  street: string;
  city: string;
  state: string;
  zipCode: string;
  fullAddress: string;
}

export class AddressParser {
  /**
   * Parse a US address string into components
   * @param address - Full address string
   * @returns ParsedAddress object with separated components
   */
  static parseAddress(address: string): ParsedAddress {
    // Remove extra whitespace and normalize
    const cleanAddress = address.trim().replace(/\s+/g, ' ');
    
    // Common US state abbreviations
    const stateAbbreviations = [
      'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
      'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
      'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
      'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
      'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
    ];

    // Try to match ZIP code pattern (5 digits or 5+4 format)
    const zipPattern = /\b(\d{5})(?:-(\d{4}))?\b/;
    const zipMatch = cleanAddress.match(zipPattern);
    
    if (!zipMatch) {
      // If no ZIP found, return basic parsing
      return {
        street: cleanAddress,
        city: '',
        state: '',
        zipCode: '',
        fullAddress: cleanAddress
      };
    }

    const zipCode = zipMatch[1] + (zipMatch[2] ? `-${zipMatch[2]}` : '');
    const addressBeforeZip = cleanAddress.substring(0, zipMatch.index).trim();
    
    // Find state abbreviation (2 capital letters before ZIP)
    let state = '';
    let city = '';
    let street = addressBeforeZip;
    
    // Look for state pattern before ZIP
    for (const stateAbbr of stateAbbreviations) {
      const statePattern = new RegExp(`\\b${stateAbbr}\\b`, 'i');
      const stateMatch = addressBeforeZip.match(statePattern);
      
      if (stateMatch && stateMatch.index !== undefined) {
        state = stateAbbr.toUpperCase();
        const beforeState = addressBeforeZip.substring(0, stateMatch.index).trim();
        const afterState = addressBeforeZip.substring(stateMatch.index + stateAbbr.length).trim();
        
        // Extract city (usually the last part before state)
        const parts = beforeState.split(',').map(part => part.trim()).filter(part => part.length > 0);
        if (parts.length > 1) {
          city = parts[parts.length - 1];
          street = parts.slice(0, -1).join(', ');
        } else {
          // If no comma, try to extract city from the end
          const words = beforeState.split(' ');
          if (words.length > 2) {
            // Assume last word or two might be city
            city = words.slice(-2).join(' ');
            street = words.slice(0, -2).join(' ');
          } else {
            city = beforeState;
            street = '';
          }
        }
        
        // Clean up any remaining parts after state
        if (afterState) {
          street = street ? `${street}, ${afterState}` : afterState;
        }
        
        break;
      }
    }
    
    // If no state found, try alternative parsing
    if (!state) {
      const parts = addressBeforeZip.split(',').map(part => part.trim());
      if (parts.length >= 2) {
        // Assume format: Street, City, State
        street = parts[0];
        city = parts[1];
        if (parts.length > 2) {
          // Try to find state in remaining parts
          for (const part of parts.slice(2)) {
            for (const stateAbbr of stateAbbreviations) {
              if (part.toUpperCase() === stateAbbr) {
                state = stateAbbr;
                break;
              }
            }
            if (state) break;
          }
        }
      } else {
        // Simple fallback
        street = addressBeforeZip;
        city = '';
      }
    }
    
    return {
      street: street || '',
      city: city || '',
      state: state || '',
      zipCode: zipCode,
      fullAddress: cleanAddress
    };
  }

  /**
   * Validate if an address appears to be a valid US address
   * @param address - Address string to validate
   * @returns boolean indicating if address is likely valid
   */
  static isValidUSAddress(address: string): boolean {
    const parsed = this.parseAddress(address);
    return !!(parsed.zipCode && parsed.state);
  }

  /**
   * Format address components back to a standard string
   * @param parsed - ParsedAddress object
   * @returns Formatted address string
   */
  static formatAddress(parsed: ParsedAddress): string {
    const parts = [];
    if (parsed.street) parts.push(parsed.street);
    if (parsed.city) parts.push(parsed.city);
    if (parsed.state) parts.push(parsed.state);
    if (parsed.zipCode) parts.push(parsed.zipCode);
    return parts.join(', ');
  }

  /**
   * Parse address and lookup ZIP code using Google Places API
   * @param address - Full address string
   * @returns Promise with parsed address including ZIP code
   */
  static async parseAddressWithZipLookup(address: string): Promise<ParsedAddress> {
    try {
      console.log('🔍 Using Google Places API for address parsing:', address);
      
      // Use Google Places API for accurate address parsing
      const googleResult = await GoogleAddressService.getAddressWithZip(address);
      
      if (googleResult) {
        console.log('✅ Google Places API result:', googleResult);
        return {
          street: googleResult.street,
          city: googleResult.city,
          state: googleResult.state,
          zipCode: googleResult.zipCode,
          fullAddress: googleResult.fullAddress
        };
      }
      
      // Fallback to basic parsing if Google API fails
      console.log('⚠️ Google Places API failed, using fallback parsing');
      const parsed = this.parseAddress(address);
      
      // Try fallback ZIP code lookup
      try {
        if (parsed.state && parsed.city) {
          const fallbackZip = ZipCodeLookup.getFallbackZipCode(parsed.city, parsed.state);
          if (fallbackZip) {
            return {
              ...parsed,
              zipCode: fallbackZip,
              fullAddress: `${parsed.street}, ${parsed.city}, ${parsed.state} ${fallbackZip}`
            };
          }
        }
      } catch (error) {
        console.error('Fallback ZIP code lookup failed:', error);
      }
      
      return parsed;
      
    } catch (error) {
      console.error('❌ Address parsing failed:', error);
      
      // Final fallback to basic parsing
      return this.parseAddress(address);
    }
  }
} 